import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axiosInstance from '../../../api/axiosInstance';

// ─────────────────────────────────────────────
//  MINI HELPERS
// ─────────────────────────────────────────────
const fmtDate = (d, withTime = false) => {
  if (!d) return 'N/A';
  const opts = { day: '2-digit', month: 'short', year: 'numeric' };
  if (withTime) { opts.hour = '2-digit'; opts.minute = '2-digit'; opts.hour12 = true; }
  return new Intl.DateTimeFormat('en-IN', opts).format(new Date(d));
};

const fmtCurrency = (n) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 }).format(Number(n) || 0);

// ─────────────────────────────────────────────
//  BADGE
// ─────────────────────────────────────────────
const Badge = ({ value }) => {
  const s = (value || 'none').toLowerCase();
  const display = s === 'none' ? 'Not Uploaded' : s.charAt(0).toUpperCase() + s.slice(1);
  const map = {
    active:    'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400',
    approved:  'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400',
    completed: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400',
    paid:      'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400',
    pending:   'bg-amber-100  text-amber-800  dark:bg-amber-900/30  dark:text-amber-400',
    due:       'bg-amber-100  text-amber-800  dark:bg-amber-900/30  dark:text-amber-400',
    overdue:   'bg-red-100    text-red-800    dark:bg-red-900/30    dark:text-red-400',
    rejected:  'bg-red-100    text-red-800    dark:bg-red-900/30    dark:text-red-400',
    inactive:  'bg-red-100    text-red-800    dark:bg-red-900/30    dark:text-red-400',
    upcoming:  'bg-slate-100  text-slate-600  dark:bg-slate-700     dark:text-slate-300',
    none:      'bg-slate-100  text-slate-500  dark:bg-slate-700     dark:text-slate-400',
  };
  const cls = map[s] || map.none;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded text-[11px] font-bold uppercase tracking-wide whitespace-nowrap ${cls}`}>
      {['active','approved','completed','paid'].includes(s) && <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 dark:bg-emerald-400" />}
      {['overdue','rejected','inactive'].includes(s)        && <span className="w-1.5 h-1.5 rounded-full bg-red-500 dark:bg-red-400" />}
      {['pending','due'].includes(s)                         && <span className="w-1.5 h-1.5 rounded-full bg-amber-500 dark:bg-amber-400" />}
      {display}
    </span>
  );
};

// ─────────────────────────────────────────────
//  SKELETON LOADER
// ─────────────────────────────────────────────
const Skeleton = ({ className = '' }) => (
  <div className={`animate-pulse rounded bg-slate-200 dark:bg-slate-700 ${className}`} />
);

// ─────────────────────────────────────────────
//  STAT CARD
// ─────────────────────────────────────────────
const StatCard = ({ label, value, sub, accent = '' }) => (
  <div className="bg-slate-50 dark:bg-slate-900/60 rounded-xl p-4 border border-slate-200 dark:border-slate-700/50">
    <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 mb-1.5 uppercase tracking-wide">{label}</p>
    <p className={`text-2xl font-bold leading-none ${accent || 'text-slate-900 dark:text-white'}`}>{value ?? '—'}</p>
    {sub && <p className="text-xs text-slate-400 dark:text-slate-500 mt-1.5">{sub}</p>}
  </div>
);

// ─────────────────────────────────────────────
//  KV ROW  (label / value pair)
// ─────────────────────────────────────────────
const KV = ({ label, children }) => (
  <div className="flex items-start justify-between py-2.5 border-b border-slate-100 dark:border-slate-700/60 last:border-0 gap-4">
    <span className="text-sm text-slate-500 dark:text-slate-400 flex-shrink-0">{label}</span>
    <span className="text-sm font-semibold text-slate-800 dark:text-slate-100 text-right break-all">{children}</span>
  </div>
);

// ─────────────────────────────────────────────
//  SECTION HEADING
// ─────────────────────────────────────────────
const SectionTitle = ({ children }) => (
  <h4 className="text-[11px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest mb-3">{children}</h4>
);

// ─────────────────────────────────────────────
//  PROGRESS BAR
// ─────────────────────────────────────────────
const ProgressBar = ({ value, max, color = 'bg-emerald-500' }) => {
  const pct = max > 0 ? Math.min(100, (value / max) * 100) : 0;
  return (
    <div className="h-1.5 w-full bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden mt-2">
      <div className={`h-full rounded-full ${color} transition-all duration-700`} style={{ width: `${pct}%` }} />
    </div>
  );
};

// ─────────────────────────────────────────────
//  TAB BUTTON
// ─────────────────────────────────────────────
// ─────────────────────────────────────────────
//  TAB BUTTON
// ─────────────────────────────────────────────
const Tab = ({ active, onClick, children, badge }) => (
  <button
    onClick={onClick}
    // Uses flex-1 so all 4 tabs divide the 100% width evenly on mobile.
    // Uses text-[10px] on small screens to prevent text wrap.
    className={`relative flex flex-1 sm:flex-none items-center justify-center gap-1 sm:gap-1.5 px-0.5 sm:px-4 py-3 text-[10px] min-[375px]:text-[11px] sm:text-sm font-semibold transition-colors border-b-2 focus:outline-none whitespace-nowrap
      ${active
        ? 'border-indigo-600 text-indigo-600 dark:border-indigo-400 dark:text-indigo-400'
        : 'border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 hover:border-slate-300 dark:hover:border-slate-600'
      }`}
  >
    {children}
    {badge != null && (
      <span className={`hidden lg:inline-flex items-center justify-center text-[10px] font-bold px-1.5 py-0.5 rounded-full ${active ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-400' : 'bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-400'}`}>
        {badge}
      </span>
    )}
  </button>
);

// ─────────────────────────────────────────────
//  INSTALLMENT STATUS COLORS
// ─────────────────────────────────────────────
const instRowCls = (status) => ({
  paid:     'bg-emerald-50/60 dark:bg-emerald-900/10',
  overdue:  'bg-red-50/60     dark:bg-red-900/10',
  due:      'bg-amber-50/60   dark:bg-amber-900/10',
  upcoming: '',
}[status] || '');


// ══════════════════════════════════════════════════════════════
//  MAIN COMPONENT
// ══════════════════════════════════════════════════════════════
const CustomerDashboard = ({ user: tableUser, onBack, rootLabel = "Admin", rootPath = "/dashboard/admin", backLabel = "All Customers" }) => {
  // ── data state ──
  const [profile,       setProfile]       = useState(null);
  const [paidInst,      setPaidInst]      = useState(null);
  const [remainingInst, setRemainingInst] = useState(null);
  const [pendingPay,    setPendingPay]    = useState(null);
  const [childCount,    setChildCount]    = useState(null);
  const [earnings,      setEarnings]      = useState(null);

  // ── ui state ──
  const [activeTab,  setActiveTab]  = useState('overview');
  const [loadingMap, setLoadingMap] = useState({ profile: true, paid: true, remaining: true, pending: true, children: true, earnings: true });
  const [errorMap,   setErrorMap]   = useState({});

  const uid = tableUser?._id || tableUser?.id;

  // 🌟 FIX 2: Bulletproof API loader
  const load = useCallback(async (key, endpoint, setter) => {
    try {
      const response = await axiosInstance.get(endpoint);
      // Safely extract data whether it's wrapped in { data: {...} } or not
      const result = response.data?.data !== undefined ? response.data.data : response.data;
      setter(result);
    } catch (e) {
      console.error(`[Dashboard] ${key} failed for ID ${uid}:`, e);
      setErrorMap(prev => ({ ...prev, [key]: true }));
    } finally {
      setLoadingMap(prev => ({ ...prev, [key]: false }));
    }
  }, [uid]);
  
    

  useEffect(() => {
    if (!uid) {
      console.warn("CustomerDashboard requires a valid user ID, but received:", tableUser);
      return; 
    }
    load('profile',   `/child/get/${uid}`,              setProfile);
    load('paid',      `/child/installments/${uid}`,     setPaidInst);
    load('remaining', `/child/remaining/${uid}`,        setRemainingInst);
    load('pending',   `/child/pay/pending/${uid}`,      setPendingPay);
    load('children',  `/child/customer-count/${uid}`,   setChildCount);
    load('earnings',  `/child/earnings/${uid}`,         setEarnings);
  }, [uid, load, tableUser]);

  // ── derived values ──
  const paidCount      = paidInst?.count            ?? 0;
  const remainCount    = remainingInst?.length       ?? (remainingInst?.count ?? 0);
  const remainList     = Array.isArray(remainingInst) ? remainingInst : (remainingInst?.installments ?? []);
  const totalInst      = paidCount + remainList.length;
  const paidAmount     = paidInst?.installments?.reduce((s, i) => s + parseFloat(i.amount || 0), 0) ?? 0;
  const overdueList    = remainList.filter(i => i.status === 'overdue');
  const dueList        = remainList.filter(i => i.status === 'due');
  const upcomingList   = remainList.filter(i => i.status === 'upcoming');
  const totalPenalty   = remainList.reduce((s, i) => s + (i.penalty || 0), 0);
  const totalOutstanding = remainList.reduce((s, i) => s + (i.total || i.amount || 0), 0);
  const isLoading      = Object.values(loadingMap).some(Boolean);
  const p              = profile || tableUser; // fallback to table data while loading

  // ── initials ──
  const initials = (p?.name || 'U').split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase();

  // ── overdue alert ──
  const hasOverdue = overdueList.length > 0;

  // ───────────────────────────────────────────
  //  LOADING SKELETON
  // ───────────────────────────────────────────
  if (loadingMap.profile && !profile) {
    return (
      <div className="animate-in fade-in duration-300 text-slate-900 dark:text-white">
        <div className="flex items-center gap-3 mb-6">
          <Skeleton className="w-16 h-16 rounded-full" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-5 w-48" />
            <Skeleton className="h-4 w-64" />
          </div>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-24 rounded-xl" />)}
        </div>
        <Skeleton className="h-64 rounded-xl" />
      </div>
    );
  }

  // ───────────────────────────────────────────
  //  RENDER
  // ───────────────────────────────────────────
  return (
    <div className="animate-in fade-in slide-in-from-right-4 duration-300 text-slate-900 dark:text-white pb-8">

      {/* ── BREADCRUMB ── */}
     {/* 🌟 THE DYNAMIC BREADCRUMB */}
     <nav className="mb-5 flex-shrink-0">
        <ol className="flex items-center whitespace-nowrap gap-1">
          <li className="inline-flex items-center">
            {/* Uses the dynamic props */}
            <Link to={rootPath} className="text-sm text-slate-500 hover:text-indigo-600 font-semibold">
              {rootLabel}
            </Link>
            <svg className="mx-2 w-3.5 h-3.5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeWidth="2.5" d="m9 18 6-6-6-6"/></svg>
          </li>
          <li className="inline-flex items-center">
            {/* Uses the dynamic props */}
            <button onClick={onBack} className="text-sm text-slate-500 hover:text-indigo-600 font-semibold">
              {backLabel}
            </button>
            <svg className="mx-2 w-3.5 h-3.5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeWidth="2.5" d="m9 18 6-6-6-6"/></svg>
          </li>
          <li className="text-sm font-bold text-slate-800 truncate">
            {tableUser?.name || 'Loading...'}
          </li>
        </ol>
      </nav>

     {/* ── PROFILE HEADER CARD (Premium Layout) ── */}
     <div className="relative overflow-hidden bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 mb-5 shadow-sm hover:shadow-md transition-shadow">
        
        {/* Subtle Background Gradient / Pattern */}
        <div className="absolute top-0 left-0 right-0 h-24 bg-gradient-to-r from-indigo-50/50 to-blue-50/50 dark:from-indigo-900/10 dark:to-blue-900/10 border-b border-slate-100 dark:border-slate-700/50"></div>

        <div className="relative p-5 sm:p-6">
          <div className="flex flex-col sm:flex-row gap-5 sm:items-start justify-between">

            {/* Left Column: Avatar + Core Info */}
            <div className="flex flex-col sm:flex-row gap-5 items-center sm:items-start">
              
              {/* Avatar (Enhanced Ring) */}
              <div className="flex-shrink-0 relative w-20 h-20 sm:w-24 sm:h-24 rounded-full ring-4 ring-white dark:ring-slate-800 shadow-md mt-2 sm:mt-0 bg-white dark:bg-slate-800">
                {/* Initials Fallback */}
                <div className="absolute inset-0 z-0 rounded-full bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center text-white text-2xl sm:text-3xl font-bold shadow-inner">
                  {initials}
                </div>
                {/* Profile Image (Sits on top layer) */}
                {uid && (
                  <img 
                    src={`${axiosInstance.defaults.baseURL}/profile/get-image/${uid}`} 
                    alt={`${p?.name} Profile`}
                    className="absolute inset-0 z-10 w-full h-full rounded-full object-cover bg-slate-100 dark:bg-slate-700"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                )}
              </div>

              {/* Name & Meta Data */}
              <div className="flex-1 min-w-0 text-center sm:text-left mt-2 sm:mt-3">
                <div className="flex flex-col sm:flex-row sm:items-center gap-2.5 mb-2 sm:mb-3">
                  <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 dark:text-white leading-none tracking-tight">
                    {p?.name}
                  </h2>
                  <div className="flex items-center justify-center sm:justify-start gap-1.5">
                    <Badge value={p?.status} />
                    <Badge value={p?.kyc} />
                  </div>
                </div>

                <div className="flex flex-wrap items-center justify-center sm:justify-start gap-x-5 gap-y-2">
                  <span className="flex items-center gap-1.5 text-sm font-medium text-slate-500 dark:text-slate-400">
                    <svg className="w-4 h-4 text-slate-400 dark:text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/></svg>
                    {p?.email}
                  </span>
                  <span className="flex items-center gap-1.5 text-sm font-medium text-slate-500 dark:text-slate-400">
                    <svg className="w-4 h-4 text-slate-400 dark:text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.948V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/></svg>
                    +91 {p?.phone}
                  </span>
                  <span className="flex items-center gap-1.5 text-sm font-medium text-slate-500 dark:text-slate-400">
                    <svg className="w-4 h-4 text-slate-400 dark:text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/></svg>
                    {p?.city || 'N/A'}
                  </span>
                </div>
              </div>
            </div>

            {/* Right Column: Actions & ID */}
            <div className="flex flex-row sm:flex-col items-center sm:items-end justify-between sm:justify-start gap-4 flex-shrink-0 pt-2 sm:pt-4">
              <button
                onClick={onBack}
                className="flex items-center gap-1.5 text-sm font-bold text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-900/20 px-4 py-2 rounded-lg hover:bg-indigo-100 dark:hover:bg-indigo-900/40 transition-colors border border-indigo-100 dark:border-indigo-800/50 shadow-sm active:scale-95"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M10 19l-7-7m0 0l7-7m-7 7h18"/></svg>
                Back to List
              </button>
              <div className="text-right">
                <p className="text-[11px] text-slate-400 dark:text-slate-500 font-mono tracking-wider">ID: {uid}</p>
                <p className="text-[11px] font-semibold text-slate-500 dark:text-slate-400 mt-0.5">Joined {fmtDate(p?.created_at)}</p>
              </div>
            </div>

          </div>

          {/* Bottom Plan Strip (Integrated neatly) */}
          <div className="mt-6 pt-4 border-t border-slate-100 dark:border-slate-700/60 flex flex-wrap items-center justify-between gap-3 bg-slate-50/50 dark:bg-slate-900/20 -mx-5 sm:-mx-6 -mb-5 sm:-mb-6 px-5 sm:px-6 py-4">
            <div className="flex flex-wrap items-center gap-2 sm:gap-3">
              <span className="text-[10px] font-extrabold text-slate-400 dark:text-slate-500 uppercase tracking-widest">Active Plan</span>
              <span className="text-sm font-bold text-slate-800 dark:text-slate-200">{profile?.plan_name || 'Standard Plan'}</span>
              <span className="text-slate-300 dark:text-slate-600 hidden sm:inline">|</span>
              <span className="text-sm font-bold text-indigo-600 dark:text-indigo-400">{fmtCurrency(profile?.plan_amt || 1000)}/month</span>
              <span className="text-slate-300 dark:text-slate-600 hidden sm:inline">|</span>
              <span className="text-xs font-mono text-slate-500 dark:text-slate-400 bg-white dark:bg-slate-800 px-2 py-0.5 rounded border border-slate-200 dark:border-slate-700 shadow-sm hidden sm:inline-block">Ref: {profile?.plan_id || p?.plan_id || '—'}</span>
            </div>
            <div className="flex items-center gap-2 ml-auto sm:ml-0">
              <span className="text-[10px] font-extrabold text-slate-400 dark:text-slate-500 uppercase tracking-widest">Bank</span>
              <Badge value={p?.bank} />
            </div>
          </div>

        </div>
      </div>

      {/* ── OVERDUE ALERT (High Visibility) ── */}
      {hasOverdue && (
        <div className="mb-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 p-4 sm:p-5 bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800/50 rounded-2xl shadow-sm relative overflow-hidden">
          {/* Subtle warning glow */}
          <div className="absolute top-0 left-0 w-1 h-full bg-red-500 shadow-[0_0_15px_rgba(239,68,68,0.5)]"></div>
          
          <div className="flex items-center gap-3.5">
            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/40 flex items-center justify-center relative">
              {/* Pulse animation ring */}
              <div className="absolute inset-0 rounded-full border border-red-400 dark:border-red-500 animate-ping opacity-75"></div>
              <svg className="w-5 h-5 text-red-600 dark:text-red-400 relative z-10" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/></svg>
            </div>
            <div>
              <p className="text-sm sm:text-base font-bold text-red-800 dark:text-red-400 leading-tight">
                Attention Required: {overdueList.length} Overdue Installment{overdueList.length > 1 ? 's' : ''}
              </p>
              <p className="text-xs sm:text-sm font-medium text-red-600 dark:text-red-500 mt-0.5">
                Total penalty accrued: <span className="font-bold">{fmtCurrency(totalPenalty)}</span>
              </p>
            </div>
          </div>
          
          <div className="w-full sm:w-auto flex justify-end">
            <span className="px-3 py-1.5 rounded-lg bg-red-600 text-white text-xs font-bold uppercase tracking-wider shadow-sm">
              Action Needed
            </span>
          </div>
        </div>
      )}

      {/* ── TABS ── */}
      {/* ── TABS ── */}
      <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden">

        {/* Tab bar - Removed overflow-x-auto, changed to flex w-full */}
        <div className="flex w-full border-b border-slate-200 dark:border-slate-700 sm:px-2">
          
          <Tab active={activeTab === 'overview'} onClick={() => setActiveTab('overview')}>
            <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"/></svg>
            Overview
          </Tab>
          
          <Tab active={activeTab === 'profile'} onClick={() => setActiveTab('profile')}>
            <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/></svg>
            {/* Hides "& KYC" on mobile to save space */}
            <span className="hidden sm:inline">Profile & KYC</span>
            <span className="sm:hidden">Profile</span>
          </Tab>
          
          <Tab active={activeTab === 'financial'} onClick={() => setActiveTab('financial')}>
            <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
            Financial
          </Tab>
          
          <Tab active={activeTab === 'installments'} onClick={() => setActiveTab('installments')} badge={totalInst || undefined}>
            <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/></svg>
            {/* Changes "Installments" to "EMIs" on mobile to save space */}
            <span className="hidden sm:inline">Installments</span>
            <span className="sm:hidden">EMIs</span>
          </Tab>
          
        </div>

        {/* ════════════════════════════════════
            TAB: OVERVIEW
        ════════════════════════════════════ */}
        {activeTab === 'overview' && (
          <div className="p-5 space-y-5">

            {/* Stat cards row */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              {loadingMap.earnings ? (
                [...Array(4)].map((_, i) => <Skeleton key={i} className="h-24 rounded-xl" />)
              ) : (
                <>
                  <StatCard
                    label="Total Earned"
                    value={fmtCurrency(earnings?.earned)}
                    sub="Lifetime commissions"
                    accent="text-emerald-600 dark:text-emerald-400"
                  />
                  <StatCard
                    label="Total Redeemed"
                    value={fmtCurrency(earnings?.redeemed)}
                    sub="Withdrawn to bank"
                    accent="text-violet-600 dark:text-violet-400"
                  />
                  <StatCard
                    label="Wallet Balance"
                    value={fmtCurrency(earnings?.balance)}
                    sub="Available to redeem"
                    accent="text-indigo-600 dark:text-indigo-400"
                  />
                  <StatCard
                    label="Direct Children"
                    value={loadingMap.children ? '…' : (childCount?.count ?? 0)}
                    sub="Referral network"
                    accent="text-amber-600 dark:text-amber-400"
                  />
                </>
              )}
            </div>

            {/* Bottom 2-col grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">

              {/* Repayment progress */}
              <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-4 space-y-4 bg-slate-50/50 dark:bg-slate-900/30">
                <SectionTitle>Repayment progress</SectionTitle>
                {loadingMap.paid || loadingMap.remaining ? (
                  <Skeleton className="h-32 rounded-lg" />
                ) : (
                  <>
                    <div className="flex items-baseline justify-between">
                      <div>
                        <span className="text-3xl font-bold text-slate-900 dark:text-white">{paidCount}</span>
                        <span className="text-base text-slate-400 dark:text-slate-500 ml-1">/ {totalInst} paid</span>
                      </div>
                      <span className="text-sm font-bold text-slate-500 dark:text-slate-400">
                        {totalInst > 0 ? ((paidCount / totalInst) * 100).toFixed(1) : 0}%
                      </span>
                    </div>
                    <ProgressBar value={paidCount} max={totalInst} />
                    <div className="grid grid-cols-2 gap-3 pt-1">
                      <div>
                        <p className="text-xs text-slate-500 dark:text-slate-400">Amount Paid</p>
                        <p className="text-sm font-bold text-emerald-600 dark:text-emerald-400">{fmtCurrency(paidAmount)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500 dark:text-slate-400">Outstanding</p>
                        <p className="text-sm font-bold text-slate-800 dark:text-slate-100">{fmtCurrency(totalOutstanding)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500 dark:text-slate-400">Penalty Accrued</p>
                        <p className={`text-sm font-bold ${totalPenalty > 0 ? 'text-red-600 dark:text-red-400' : 'text-slate-400'}`}>{fmtCurrency(totalPenalty)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500 dark:text-slate-400">Pending Payments</p>
                        <p className={`text-sm font-bold ${(pendingPay?.count || 0) > 0 ? 'text-amber-600 dark:text-amber-400' : 'text-slate-400'}`}>{loadingMap.pending ? '…' : (pendingPay?.count ?? 0)}</p>
                      </div>
                    </div>
                  </>
                )}
              </div>

              {/* Next dues */}
              <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-4 bg-slate-50/50 dark:bg-slate-900/30">
                <SectionTitle>Upcoming dues</SectionTitle>
                {loadingMap.remaining ? (
                  <div className="space-y-2">
                    {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-16 rounded-lg" />)}
                  </div>
                ) : (
                  <div className="space-y-2">
                    {/* Overdue */}
                    {overdueList.slice(0, 2).map(inst => (
                      <div key={inst.no} className="flex items-center justify-between p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-800/40">
                        <div>
                          <p className="text-sm font-bold text-red-700 dark:text-red-400">Installment #{String(inst.no).padStart(2, '0')}</p>
                          <p className="text-xs text-red-500 mt-0.5">{inst.overdue} days overdue · Deadline {fmtDate(inst.deadline)}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-bold text-red-700 dark:text-red-400">{fmtCurrency(inst.total)}</p>
                          {inst.penalty > 0 && <p className="text-xs text-red-500">+{fmtCurrency(inst.penalty)} penalty</p>}
                        </div>
                      </div>
                    ))}
                    {/* Due */}
                    {dueList.slice(0, 1).map(inst => (
                      <div key={inst.no} className="flex items-center justify-between p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-100 dark:border-amber-800/40">
                        <div>
                          <p className="text-sm font-bold text-amber-700 dark:text-amber-400">Installment #{String(inst.no).padStart(2, '0')}</p>
                          <p className="text-xs text-amber-600 dark:text-amber-500 mt-0.5">Due {fmtDate(inst.due)} · Deadline {fmtDate(inst.deadline)}</p>
                        </div>
                        <p className="text-sm font-bold text-amber-700 dark:text-amber-400">{fmtCurrency(inst.total)}</p>
                      </div>
                    ))}
                    {/* Next upcoming */}
                    {upcomingList.slice(0, 1).map(inst => (
                      <div key={inst.no} className="flex items-center justify-between p-3 rounded-lg bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
                        <div>
                          <p className="text-sm font-semibold text-slate-700 dark:text-slate-300">Installment #{String(inst.no).padStart(2, '0')} · Next upcoming</p>
                          <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">Due {fmtDate(inst.due)}</p>
                        </div>
                        <p className="text-sm font-bold text-slate-700 dark:text-slate-200">{fmtCurrency(inst.amount)}</p>
                      </div>
                    ))}
                    {overdueList.length === 0 && dueList.length === 0 && upcomingList.length === 0 && (
                      <p className="text-sm text-slate-400 dark:text-slate-500 text-center py-6">No pending dues</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ════════════════════════════════════
            TAB: PROFILE & KYC
        ════════════════════════════════════ */}
        {/* ════════════════════════════════════
            TAB: PROFILE & KYC
        ════════════════════════════════════ */}
        {activeTab === 'profile' && (
          <div className="p-5">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

              {/* ── 1. PERSONAL INFO CARD ── */}
              <div className="bg-white dark:bg-slate-800/80 rounded-2xl border border-slate-200 dark:border-slate-700 p-6 shadow-sm hover:shadow-md transition-shadow">
                
                {/* Header */}
                <div className="flex items-center gap-3 mb-6 border-b border-slate-100 dark:border-slate-700/60 pb-4">
                  <div className="p-2.5 bg-violet-50 dark:bg-violet-900/20 text-violet-600 dark:text-violet-400 rounded-xl">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-800 dark:text-slate-100">Personal Information</h3>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">Basic identity and contact details.</p>
                  </div>
                </div>

                {/* Content */}
                <div className="space-y-1">
                  <KV label="Full Name">
                    <span className="text-slate-900 dark:text-white">{p?.name}</span>
                  </KV>
                  <KV label="User ID">
                    <span className="font-mono text-xs bg-slate-100 dark:bg-slate-800 px-2.5 py-1 rounded-md text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-600">
                      {uid}
                    </span>
                  </KV>
                  <KV label="Email">
                    <span className="flex items-center justify-end gap-1.5 text-slate-700 dark:text-slate-200">
                      <svg className="w-3.5 h-3.5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/></svg>
                      {p?.email}
                    </span>
                  </KV>
                  <KV label="Phone">
                    <span className="flex items-center justify-end gap-1.5 text-slate-700 dark:text-slate-200">
                      <svg className="w-3.5 h-3.5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.948V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/></svg>
                      +91 {p?.phone}
                    </span>
                  </KV>
                  <KV label="City">
                    <span className="flex items-center justify-end gap-1.5 text-slate-700 dark:text-slate-200">
                      <svg className="w-3.5 h-3.5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/></svg>
                      {p?.city || '—'}
                    </span>
                  </KV>
                  <KV label="Registered On">
                    <span className="flex items-center justify-end gap-1.5 text-slate-700 dark:text-slate-200">
                      <svg className="w-3.5 h-3.5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/></svg>
                      {fmtDate(p?.created_at, true)}
                    </span>
                  </KV>
                </div>
              </div>

              {/* ── 2. STATUS & PLAN CARD ── */}
              <div className="bg-white dark:bg-slate-800/80 rounded-2xl border border-slate-200 dark:border-slate-700 p-6 shadow-sm hover:shadow-md transition-shadow flex flex-col">
                
                {/* Header */}
                <div className="flex items-center gap-3 mb-5 border-b border-slate-100 dark:border-slate-700/60 pb-4">
                  <div className="p-2.5 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400 rounded-xl">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-800 dark:text-slate-100">Verification & Plan</h3>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">Current account status and subscriptions.</p>
                  </div>
                </div>

                {/* Badges Grid (Replaces KV for higher visibility) */}
                <div className="grid grid-cols-3 gap-3 mb-6">
                  <div className="flex flex-col items-center justify-center p-3 rounded-xl bg-slate-50 dark:bg-slate-900/50 border border-slate-100 dark:border-slate-700/60">
                    <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider mb-2">Account</span>
                    <Badge value={p?.status} />
                  </div>
                  <div className="flex flex-col items-center justify-center p-3 rounded-xl bg-slate-50 dark:bg-slate-900/50 border border-slate-100 dark:border-slate-700/60">
                    <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider mb-2">KYC Docs</span>
                    <Badge value={p?.kyc} />
                  </div>
                  <div className="flex flex-col items-center justify-center p-3 rounded-xl bg-slate-50 dark:bg-slate-900/50 border border-slate-100 dark:border-slate-700/60">
                    <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider mb-2">Bank Data</span>
                    <Badge value={p?.bank} />
                  </div>
                </div>

                {/* Highlighted Subscription Panel */}
                <div className="relative p-5 mt-auto rounded-xl bg-gradient-to-br from-indigo-50 to-blue-50 dark:from-indigo-900/20 dark:to-blue-900/10 border border-indigo-100 dark:border-indigo-800/30 overflow-hidden">
                  {/* Decorative Icon */}
                  <div className="absolute -top-3 -right-3 opacity-5 dark:opacity-10 pointer-events-none">
                    <svg className="w-24 h-24 text-indigo-600 dark:text-indigo-400" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" /></svg>
                  </div>
                  
                  <div className="relative z-10 space-y-1">
                    <h4 className="text-xs font-bold text-indigo-800 dark:text-indigo-300 uppercase tracking-widest mb-3 flex items-center gap-2">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" /></svg>
                      Active Plan
                    </h4>
                    <KV label="Plan Name"><span className="text-indigo-900 dark:text-indigo-100 font-bold">{profile?.plan_name || '—'}</span></KV>
                    <KV label="Plan ID"><span className="font-mono text-[10px] bg-indigo-100/50 dark:bg-indigo-900/50 text-indigo-700 dark:text-indigo-300 px-2 py-0.5 rounded border border-indigo-200/50 dark:border-indigo-800/50">{profile?.plan_id || p?.plan_id || '—'}</span></KV>
                    <KV label="Monthly EMI"><span className="text-indigo-900 dark:text-indigo-100 font-bold">{fmtCurrency(profile?.plan_amt || 1000)}</span></KV>
                    <KV label="Total Tenure"><span className="text-indigo-900 dark:text-indigo-100">{totalInst > 0 ? `${totalInst} installments` : '24 installments'}</span></KV>
                  </div>
                </div>
              </div>

              {/* ── 3. UPLINE / SPONSOR CARD (Spans 2 columns) ── */}
              <div className="lg:col-span-2 bg-white dark:bg-slate-800/80 rounded-2xl border border-slate-200 dark:border-slate-700 p-6 shadow-sm hover:shadow-md transition-shadow">
                
                {/* Header */}
                <div className="flex items-center gap-3 mb-6 border-b border-slate-100 dark:border-slate-700/60 pb-4">
                  <div className="p-2.5 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 rounded-xl">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" /></svg>
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-800 dark:text-slate-100">Upline & Sponsorship</h3>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">The parent network node this user is registered under.</p>
                  </div>
                </div>

                {/* Upline Details Banner */}
                <div className="flex flex-col md:flex-row items-center gap-6 p-5 bg-slate-50 dark:bg-slate-900/40 rounded-xl border border-slate-100 dark:border-slate-700/60">
                  
                  {/* Parent Identity */}
                  <div className="flex items-center gap-4 min-w-[240px]">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-lg font-bold text-white shadow-md border-2 border-white dark:border-slate-800 flex-shrink-0">
                      {(profile?.parent_name || 'A').split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2)}
                    </div>
                    <div>
                      <p className="font-bold text-lg text-slate-900 dark:text-white leading-tight mb-1.5">{profile?.parent_name || '—'}</p>
                      <Badge value={profile?.parent_status || 'active'} />
                    </div>
                  </div>

                  {/* Vertical Divider (Desktop) / Horizontal (Mobile) */}
                  <div className="h-px md:h-12 w-full md:w-px bg-slate-200 dark:bg-slate-700 flex-shrink-0"></div>

                  {/* Parent Meta Info */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-8 gap-y-4 flex-1 w-full">
                    <div>
                      <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider mb-1.5">Email Address</p>
                      <p className="text-sm font-semibold text-slate-700 dark:text-slate-300 flex items-center gap-1.5">
                        <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/></svg>
                        {profile?.parent_email || '—'}
                      </p>
                    </div>
                    <div>
                      <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider mb-1.5">Phone Number</p>
                      <p className="text-sm font-semibold text-slate-700 dark:text-slate-300 flex items-center gap-1.5">
                        <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.948V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/></svg>
                        {profile?.parent_phone ? `+91 ${profile.parent_phone}` : '—'}
                      </p>
                    </div>
                    <div>
                      <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider mb-1.5">Parent Since</p>
                      <p className="text-sm font-semibold text-slate-700 dark:text-slate-300 flex items-center gap-1.5">
                        <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/></svg>
                        {fmtDate(profile?.parent_created_at)}
                      </p>
                    </div>
                  </div>
                  
                </div>
              </div>

            </div>
          </div>
        )}

        {/* ════════════════════════════════════
            TAB: FINANCIAL
        ════════════════════════════════════ */}
        {/* ════════════════════════════════════
            TAB: FINANCIAL
        ════════════════════════════════════ */}
        {activeTab === 'financial' && (
          <div className="p-5 space-y-6">

            {/* ── 1. EARNINGS STAT CARDS ── */}
            {loadingMap.earnings ? (
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-28 rounded-2xl" />)}
              </div>
            ) : (
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard label="Total Earned"   value={fmtCurrency(earnings?.earned)}   sub="Lifetime referral commissions" accent="text-emerald-600 dark:text-emerald-400" />
                <StatCard label="Total Redeemed" value={fmtCurrency(earnings?.redeemed)} sub="Successfully withdrawn"          accent="text-violet-600 dark:text-violet-400" />
                <StatCard label="Wallet Balance" value={fmtCurrency(earnings?.balance)}  sub="Available to redeem"             accent="text-indigo-600 dark:text-indigo-400" />
                <StatCard label="Forfeited"      value={fmtCurrency(earnings?.forfeited)} sub="Lapsed/cancelled rewards"       accent="text-slate-500 dark:text-slate-400" />
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

              {/* ── 2. INSTALLMENT SUMMARY CARD ── */}
              <div className="bg-white dark:bg-slate-800/80 rounded-2xl border border-slate-200 dark:border-slate-700 p-6 shadow-sm hover:shadow-md transition-shadow flex flex-col">
                
                {/* Header */}
                <div className="flex items-center gap-3 mb-6 border-b border-slate-100 dark:border-slate-700/60 pb-4">
                  <div className="p-2.5 bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400 rounded-xl">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-800 dark:text-slate-100">Payment Summary</h3>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">Overview of EMI progress and dues.</p>
                  </div>
                </div>

                {loadingMap.paid || loadingMap.remaining ? (
                  <Skeleton className="h-48 rounded-xl" />
                ) : (
                  <>
                    {/* Core Financials */}
                    <div className="space-y-1 mb-6">
                      <KV label="Total Installments"><span className="text-slate-900 dark:text-white font-semibold">{totalInst}</span></KV>
                      <KV label="Installments Paid"><span className="text-emerald-600 dark:text-emerald-400 font-bold">{paidCount}</span></KV>
                      <KV label="Amount Paid"><span className="text-emerald-600 dark:text-emerald-400 font-bold">{fmtCurrency(paidAmount)}</span></KV>
                      <KV label="Outstanding Balance"><span className="text-slate-900 dark:text-white font-bold">{fmtCurrency(totalOutstanding)}</span></KV>
                      <KV label="Remaining Tenure"><span className="text-slate-700 dark:text-slate-300">{remainList.length} months</span></KV>
                    </div>

                    {/* Alerts / Action Required Panel */}
                    <div className="mt-auto bg-slate-50 dark:bg-slate-900/40 rounded-xl border border-slate-100 dark:border-slate-700/50 p-4">
                      <h4 className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest mb-3 flex items-center gap-1.5">
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" /></svg>
                        Action Items
                      </h4>
                      
                      <div className="grid grid-cols-2 gap-3 mb-3">
                        <div className={`p-3 rounded-lg border transition-colors ${overdueList.length > 0 ? 'bg-red-50/50 dark:bg-red-900/20 border-red-100 dark:border-red-800/40' : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700'}`}>
                          <p className="text-[10px] font-semibold text-slate-500 uppercase mb-0.5">Overdue EMIs</p>
                          <p className={`text-lg font-bold ${overdueList.length > 0 ? 'text-red-600 dark:text-red-400' : 'text-slate-700 dark:text-slate-300'}`}>{overdueList.length}</p>
                        </div>
                        <div className={`p-3 rounded-lg border transition-colors ${totalPenalty > 0 ? 'bg-red-50/50 dark:bg-red-900/20 border-red-100 dark:border-red-800/40' : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700'}`}>
                          <p className="text-[10px] font-semibold text-slate-500 uppercase mb-0.5">Total Penalty</p>
                          <p className={`text-lg font-bold ${totalPenalty > 0 ? 'text-red-600 dark:text-red-400' : 'text-slate-700 dark:text-slate-300'}`}>{fmtCurrency(totalPenalty)}</p>
                        </div>
                      </div>

                      <div className={`flex items-center justify-between p-3 rounded-lg border transition-colors ${(pendingPay?.count || 0) > 0 ? 'bg-amber-50/50 dark:bg-amber-900/20 border-amber-100 dark:border-amber-800/40' : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700'}`}>
                        <span className="text-xs font-semibold text-slate-600 dark:text-slate-400">Pending Verification</span>
                        <span className={`text-sm font-bold ${(pendingPay?.count || 0) > 0 ? 'text-amber-600 dark:text-amber-400' : 'text-slate-700 dark:text-slate-300'}`}>
                          {loadingMap.pending ? '…' : (pendingPay?.count ?? 0)} payments
                        </span>
                      </div>
                    </div>
                  </>
                )}
              </div>

              {/* ── 3. PAID RECEIPTS LEDGER ── */}
              <div className="bg-white dark:bg-slate-800/80 rounded-2xl border border-slate-200 dark:border-slate-700 p-6 shadow-sm hover:shadow-md transition-shadow flex flex-col max-h-[600px]">
                
                {/* Header */}
                <div className="flex items-center gap-3 mb-4 border-b border-slate-100 dark:border-slate-700/60 pb-4 flex-shrink-0">
                  <div className="p-2.5 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400 rounded-xl">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-800 dark:text-slate-100">Transaction Ledger</h3>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">History of successfully paid installments.</p>
                  </div>
                </div>

                {/* Ledger Content */}
                {loadingMap.paid ? (
                  <div className="space-y-3">
                    {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-16 rounded-xl" />)}
                  </div>
                ) : paidInst?.installments?.length > 0 ? (
                  <div className="space-y-1 overflow-y-auto custom-scrollbar pr-2 -mr-2">
                    {paidInst.installments.map((inst) => (
                      <div key={inst.id} className="group flex items-center gap-4 p-3 rounded-xl hover:bg-slate-50 dark:hover:bg-slate-700/30 transition-colors border border-transparent hover:border-slate-200 dark:hover:border-slate-600/50">
                        
                        {/* Success Icon */}
                        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-emerald-100 dark:bg-emerald-900/40 flex items-center justify-center text-emerald-600 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800/50 group-hover:scale-110 transition-transform">
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7"/></svg>
                        </div>
                        
                        {/* Details */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-0.5">
                            <p className="text-sm font-bold text-slate-900 dark:text-white truncate">
                              Installment #{String(inst.installment_no).padStart(2, '0')}
                            </p>
                            <p className="text-sm font-bold text-emerald-600 dark:text-emerald-400 ml-3">
                              {fmtCurrency(inst.amount)}
                            </p>
                          </div>
                          <div className="flex items-center justify-between mt-1">
                            <p className="text-xs text-slate-500 dark:text-slate-400 flex items-center gap-1">
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                              {fmtDate(inst.paid_at, true)}
                            </p>
                            <span className="text-[10px] font-mono text-slate-500 bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded border border-slate-200 dark:border-slate-700 truncate max-w-[100px] sm:max-w-[140px]" title={inst.payment_id}>
                              Ref: {inst.payment_id}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-12 px-4 text-center h-full">
                    <div className="w-16 h-16 bg-slate-50 dark:bg-slate-800 rounded-full flex items-center justify-center mb-4">
                      <svg className="w-8 h-8 text-slate-300 dark:text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/></svg>
                    </div>
                    <p className="text-sm font-bold text-slate-700 dark:text-slate-300 mb-1">No Transactions Yet</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">Approved installment payments will appear here as a ledger.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ════════════════════════════════════
            TAB: INSTALLMENTS
        ════════════════════════════════════ */}
        {/* ════════════════════════════════════
            TAB: INSTALLMENTS
        ════════════════════════════════════ */}
        {activeTab === 'installments' && (
          <div className="p-5 space-y-6">

            {/* Main Table Card */}
            <div className="bg-white dark:bg-slate-800/80 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm flex flex-col overflow-hidden">
              
              {/* Header & Status Chips */}
              <div className="p-5 sm:p-6 border-b border-slate-100 dark:border-slate-700/60 flex flex-col xl:flex-row xl:items-center justify-between gap-5 bg-slate-50/50 dark:bg-slate-900/20">
                
                {/* Title */}
                <div className="flex items-center gap-3">
                  <div className="p-2.5 bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400 rounded-xl flex-shrink-0">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-800 dark:text-slate-100">Installment Schedule</h3>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">Complete breakdown of all past and future EMIs.</p>
                  </div>
                </div>

                {/* Summary Chips */}
                <div className="flex flex-wrap gap-2 items-center">
                  {paidCount > 0    && <span className="flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-lg border border-emerald-200 dark:border-emerald-800/50 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 shadow-sm"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />{paidCount} paid</span>}
                  {overdueList.length > 0 && <span className="flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-lg border border-red-200 dark:border-red-800/50 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 shadow-sm"><span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />{overdueList.length} overdue</span>}
                  {dueList.length > 0     && <span className="flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-lg border border-amber-200 dark:border-amber-800/50 bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 shadow-sm"><span className="w-1.5 h-1.5 rounded-full bg-amber-500" />{dueList.length} due</span>}
                  {upcomingList.length > 0 && <span className="flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 text-slate-600 dark:text-slate-300 shadow-sm">{upcomingList.length} upcoming</span>}
                </div>

              </div>

              {/* Table Area */}
              {loadingMap.paid || loadingMap.remaining ? (
                <div className="p-5">
                  <Skeleton className="h-64 rounded-xl" />
                </div>
              ) : (
                <div className="overflow-x-auto max-h-[500px] overflow-y-auto custom-scrollbar">
                  <table className="w-full text-left border-collapse whitespace-nowrap min-w-max">
                    <thead className="sticky top-0 z-10">
                      <tr className="bg-slate-100/90 dark:bg-slate-900/90 backdrop-blur-sm border-b border-slate-200 dark:border-slate-700 shadow-sm">
                        <th className="px-5 py-3.5 text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest">#</th>
                        <th className="px-5 py-3.5 text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest">Due Date</th>
                        <th className="px-5 py-3.5 text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest">Deadline</th>
                        <th className="px-5 py-3.5 text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest text-right">Amount</th>
                        <th className="px-5 py-3.5 text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest text-right">Penalty</th>
                        <th className="px-5 py-3.5 text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest text-right">Total</th>
                        <th className="px-5 py-3.5 text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest text-center">Status</th>
                        <th className="px-5 py-3.5 text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest text-center">Overdue Days</th>
                        <th className="px-5 py-3.5 text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-widest">Payment Ref</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 dark:divide-slate-700/50">

                      {/* Paid rows */}
                      {paidInst?.installments?.map((inst) => (
                        <tr key={`paid-${inst.installment_no}`} className={`transition-colors hover:bg-emerald-50/50 dark:hover:bg-emerald-900/20 ${instRowCls('paid')}`}>
                          <td className="px-5 py-3.5 text-sm font-bold text-slate-500 dark:text-slate-400">{String(inst.installment_no).padStart(2, '0')}</td>
                          <td className="px-5 py-3.5 text-sm font-medium text-slate-700 dark:text-slate-300">{fmtDate(inst.paid_at)}</td>
                          <td className="px-5 py-3.5 text-sm text-slate-400 dark:text-slate-500">—</td>
                          <td className="px-5 py-3.5 text-sm font-semibold text-right text-slate-800 dark:text-slate-100">{fmtCurrency(inst.amount)}</td>
                          <td className="px-5 py-3.5 text-sm text-right text-slate-400 dark:text-slate-500">—</td>
                          <td className="px-5 py-3.5 text-sm font-bold text-right text-emerald-600 dark:text-emerald-400">{fmtCurrency(inst.amount)}</td>
                          <td className="px-5 py-3.5 text-center"><Badge value="paid" /></td>
                          <td className="px-5 py-3.5 text-sm text-center text-slate-400 dark:text-slate-500">—</td>
                          <td className="px-5 py-3.5">
                            <span className="text-[10px] font-mono text-slate-500 bg-white dark:bg-slate-800 px-2 py-1 rounded-md border border-slate-200 dark:border-slate-700 truncate inline-block max-w-[140px]" title={inst.payment_id}>
                              {inst.payment_id}
                            </span>
                          </td>
                        </tr>
                      ))}

                      {/* Remaining rows */}
                      {remainList.map((inst) => (
                        <tr key={`rem-${inst.no}`} className={`transition-colors hover:opacity-90 ${instRowCls(inst.status)}`}>
                          <td className="px-5 py-3.5 text-sm font-bold text-slate-500 dark:text-slate-400">{String(inst.no).padStart(2, '0')}</td>
                          <td className="px-5 py-3.5 text-sm font-medium text-slate-700 dark:text-slate-300">{fmtDate(inst.due)}</td>
                          <td className="px-5 py-3.5 text-sm font-medium text-slate-500 dark:text-slate-400">{fmtDate(inst.deadline)}</td>
                          <td className="px-5 py-3.5 text-sm font-semibold text-right text-slate-800 dark:text-slate-100">{fmtCurrency(inst.amount)}</td>
                          <td className={`px-5 py-3.5 text-sm font-semibold text-right ${inst.penalty > 0 ? 'text-red-600 dark:text-red-400' : 'text-slate-400 dark:text-slate-500'}`}>
                            {inst.penalty > 0 ? `+${fmtCurrency(inst.penalty)}` : '—'}
                          </td>
                          <td className="px-5 py-3.5 text-sm font-bold text-right text-slate-800 dark:text-slate-100">{fmtCurrency(inst.total)}</td>
                          <td className="px-5 py-3.5 text-center"><Badge value={inst.status} /></td>
                          <td className="px-5 py-3.5 text-center">
                            {inst.overdue > 0 ? (
                              <span className="text-xs font-bold text-red-600 dark:text-red-400 bg-red-100 dark:bg-red-900/30 px-2 py-0.5 rounded-md">
                                {inst.overdue}d
                              </span>
                            ) : (
                              <span className="text-sm text-slate-400 dark:text-slate-500">—</span>
                            )}
                          </td>
                          <td className="px-5 py-3.5 text-sm text-slate-400 dark:text-slate-500">—</td>
                        </tr>
                      ))}

                      {totalInst === 0 && (
                        <tr>
                          <td colSpan="9" className="px-5 py-20 text-center">
                            <svg className="w-10 h-10 mx-auto text-slate-300 dark:text-slate-600 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/></svg>
                            <p className="text-sm font-bold text-slate-500 dark:text-slate-400">No installments found.</p>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Bottom Summary Bar */}
              {!loadingMap.paid && !loadingMap.remaining && totalInst > 0 && (
                <div className="bg-slate-50 dark:bg-slate-900/40 p-4 sm:p-5 border-t border-slate-200 dark:border-slate-700 flex flex-col sm:flex-row items-center justify-between gap-4">
                  <p className="text-sm font-semibold text-slate-500 dark:text-slate-400">
                    Showing <span className="text-slate-800 dark:text-white">{totalInst}</span> total installments
                  </p>
                  
                  <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] uppercase font-bold text-slate-400 tracking-wider">Total Paid</span>
                      <span className="text-sm font-bold text-emerald-600 dark:text-emerald-400">{fmtCurrency(paidAmount)}</span>
                    </div>
                    <div className="w-px h-4 bg-slate-300 dark:bg-slate-600 hidden sm:block"></div>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] uppercase font-bold text-slate-400 tracking-wider">Outstanding</span>
                      <span className="text-sm font-bold text-slate-800 dark:text-slate-100">{fmtCurrency(totalOutstanding)}</span>
                    </div>
                    {totalPenalty > 0 && (
                      <>
                        <div className="w-px h-4 bg-slate-300 dark:bg-slate-600 hidden sm:block"></div>
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] uppercase font-bold text-slate-400 tracking-wider">Penalty</span>
                          <span className="text-sm font-bold text-red-600 dark:text-red-400">+{fmtCurrency(totalPenalty)}</span>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>

          </div>
        )}
      </div>
    </div>
  );
};

export default CustomerDashboard;